import { useState } from "react";
import { X, Trash2, AlertTriangle, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

/**
 * Modal shown when a user taps a receipt with scan_status === "failed".
 *
 * Shows the captured photo and a Delete action with inline confirmation.
 */
export default function CouldntReadModal({ receipt, isOpen, onClose, onChanged }) {
    const [isDeleting, setIsDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    if (!isOpen || !receipt) return null;

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            const resp = await axios.delete(`${API}/receipts/${receipt.id}`, {
                withCredentials: true,
                timeout: 15000,
            });
            if (resp.data?.success || resp.status === 200) {
                toast.success("Receipt deleted");
                onChanged?.();
                onClose();
            } else {
                toast.error("Couldn't delete. Please try again.");
            }
        } catch (err) {
            console.error("Delete receipt error:", err);
            toast.error("Couldn't delete. Please try again.");
        } finally {
            setIsDeleting(false);
            setConfirmDelete(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    const imageSrc =
        receipt.receipt_image_url ||
        receipt.receipt_image_base64 ||
        null;

    return (
        <div
            onClick={handleOverlayClick}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{ backgroundColor: "rgba(15,23,42,0.55)", backdropFilter: "blur(2px)" }}
            data-testid="couldnt-read-overlay"
        >
            <style>{`
        @keyframes mb-slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        @keyframes mb-scale-in {
          from { opacity: 0; transform: scale(0.96); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>

            <div
                className="bg-white flex flex-col w-full sm:max-w-md"
                style={{
                    maxHeight: "92vh",
                    borderTopLeftRadius: 22,
                    borderTopRightRadius: 22,
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    overflow: "hidden",
                    boxShadow: "0 -8px 30px rgba(0,0,0,0.18)",
                    animation: "mb-slide-up 0.28s ease-out",
                }}
                data-testid="couldnt-read-sheet"
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between px-5 py-4"
                    style={{
                        background: `linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)`,
                        borderBottom: "1px solid #FCA5A5",
                    }}
                >
                    <div className="flex items-center gap-2.5">
                        <AlertTriangle className="w-5 h-5" style={{ color: "#B91C1C" }} strokeWidth={2.2} />
                        <h2
                            style={{
                                color: "#7F1D1D",
                                fontSize: 17,
                                fontWeight: 700,
                                fontFamily: "Manrope, sans-serif",
                            }}
                        >
                            Couldn't read this receipt
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="flex items-center justify-center"
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: 999,
                            backgroundColor: "rgba(255,255,255,0.6)",
                            color: "#7F1D1D",
                        }}
                        aria-label="Close"
                        data-testid="couldnt-read-close-btn"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto" style={{ padding: "18px 18px 0 18px" }}>
                    <p
                        style={{
                            color: "#374151",
                            fontSize: 14,
                            fontFamily: "DM Sans, sans-serif",
                            lineHeight: 1.55,
                            marginBottom: 14,
                        }}
                    >
                        We couldn't read this receipt. Here's what you captured:
                    </p>

                    {/* Photo */}
                    <div
                        className="w-full mb-4 flex items-center justify-center rounded-xl"
                        style={{
                            backgroundColor: "#F1F5F9",
                            border: "1px solid #E5E7EB",
                            padding: 8,
                            minHeight: 200,
                        }}
                    >
                        {imageSrc ? (
                            <img
                                src={imageSrc}
                                alt="Captured receipt"
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: 360,
                                    objectFit: "contain",
                                    borderRadius: 10,
                                    imageOrientation: "from-image",
                                }}
                            />
                        ) : (
                            <p
                                style={{
                                    color: "#9CA3AF",
                                    fontSize: 13,
                                    fontFamily: "DM Sans, sans-serif",
                                }}
                            >
                                No image available
                            </p>
                        )}
                    </div>

                    <p
                        style={{
                            color: "#6B7280",
                            fontSize: 12,
                            fontFamily: "DM Sans, sans-serif",
                            lineHeight: 1.5,
                            marginBottom: 14,
                        }}
                    >
                        If the image is unclear, you can delete and try scanning a new photo from the receipts screen.
                    </p>
                </div>

                {/* Actions */}
                <div
                    className="flex flex-col gap-2"
                    style={{ padding: "12px 18px 18px 18px", borderTop: "1px solid #F3F4F6" }}
                >
                    {!confirmDelete ? (
                        <>
                            <button
                                onClick={() => setConfirmDelete(true)}
                                disabled={isDeleting}
                                className="w-full flex items-center justify-center gap-2 rounded-xl transition-colors hover:bg-red-50"
                                style={{
                                    backgroundColor: "#FFFFFF",
                                    color: "#DC2626",
                                    padding: "14px 16px",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    fontFamily: "DM Sans, sans-serif",
                                    border: "1px solid #FECACA",
                                }}
                                data-testid="couldnt-read-delete-btn"
                            >
                                <Trash2 className="w-4 h-4" />
                                Delete
                            </button>
                        </>
                    ) : (
                        <>
                            <div
                                className="rounded-xl"
                                style={{
                                    backgroundColor: "#FEF2F2",
                                    border: "1px solid #FECACA",
                                    padding: "12px 14px",
                                    marginBottom: 4,
                                }}
                            >
                                <p
                                    style={{
                                        color: "#7F1D1D",
                                        fontSize: 13,
                                        fontWeight: 600,
                                        fontFamily: "DM Sans, sans-serif",
                                        lineHeight: 1.5,
                                    }}
                                >
                                    Delete this receipt? This can't be undone.
                                </p>
                            </div>
                            <button
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className="w-full flex items-center justify-center gap-2 rounded-xl"
                                style={{
                                    backgroundColor: "#DC2626",
                                    color: "#FFFFFF",
                                    padding: "14px 16px",
                                    fontSize: 14,
                                    fontWeight: 700,
                                    fontFamily: "DM Sans, sans-serif",
                                    opacity: isDeleting ? 0.7 : 1,
                                    boxShadow: "0 4px 12px rgba(220,38,38,0.25)",
                                }}
                                data-testid="couldnt-read-confirm-delete-btn"
                            >
                                {isDeleting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Trash2 className="w-4 h-4" />
                                )}
                                {isDeleting ? "Deleting…" : "Yes, delete"}
                            </button>
                            <button
                                onClick={() => setConfirmDelete(false)}
                                disabled={isDeleting}
                                className="w-full flex items-center justify-center rounded-xl transition-colors hover:bg-gray-100"
                                style={{
                                    backgroundColor: "#FFFFFF",
                                    color: "#374151",
                                    padding: "12px 16px",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    fontFamily: "DM Sans, sans-serif",
                                    border: "1px solid #E5E7EB",
                                }}
                            >
                                Cancel
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}